import './travel-card.scss'
import React, { useState } from 'react'
import { EmphasizedText, Paragraph, StrongText } from '@entur/typography'
import { colors, space } from '@entur/tokens'
import { BaseExpand, ExpandArrow } from '@entur/expand'
import { SmallAlertBox } from '@entur/alert'
import { SavingTicket } from '../../../api-clients/api-types'
import { toPrintFormat } from '../../../utils/date-utils'
import { TicketDetails } from './ticket-details'
import { calculatePeriod, isPeriodTicket, savingPrintFormat } from '../savings-calculations'
import { SavingsDetailTable } from './savings-detail-table'
import { RefundBadge } from '../refund-badge'

type TravelCardProps = {
  ticket: SavingTicket
  year: number
  month: number
}

export const TravelCard: React.FC<TravelCardProps> = (props) => {
  const [open, setOpen] = useState(false)

  const headerStyle = open ? { backgroundColor: colors.greys.grey80 } : {}

  const savingTicket = props.ticket
  const savingForMonth = savingTicket.savingDistribution?.find(
    (monthlySaving) => monthlySaving.month === props.month && monthlySaving.year === props.year
  )?.details
  const isNotDistributed = savingForMonth === undefined
  const isSingleTicketAndCancelledAnotherMonth =
    !isPeriodTicket(savingTicket) && savingTicket.savingDistribution.length > 1

  const savingDetails =
    isNotDistributed || isSingleTicketAndCancelledAnotherMonth ? savingTicket.totalSaving : savingForMonth

  const periodCalculations = calculatePeriod(savingTicket, props.month, props.year)

  const totalTravelDaysForTicket = Math.round(
    (savingTicket.lastTravelDate.getTime() - savingTicket.travelDate.getTime()) / (1000 * 24 * 3600) + 1
  )

  return (
    <article data-cy="travel-card" className="travel-card">
      <header
        className="ticket-header"
        role="button"
        onClick={() => {
          setOpen(!open)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') setOpen(!open)
        }}
        tabIndex={0}
        style={headerStyle}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Paragraph as="h3" className="ticket-header-title">
            <RefundBadge month={props.month} ticket={props.ticket} />
            {props.ticket?.pathDescription}
          </Paragraph>
          <div>
            <Paragraph
              style={{
                margin: '0',
                display: 'flex',
                flex: '1',
              }}
            >
              {isNotDistributed && props.month !== 0 ? (
                <EmphasizedText>Ikke tilgjengelig</EmphasizedText>
              ) : (
                <StrongText>{savingPrintFormat(savingDetails.saving)}</StrongText>
              )}
              <ExpandArrow style={{ marginLeft: space.medium }} open={open} />
            </Paragraph>
          </div>
        </div>
        <p className="ticket-tag">
          {periodCalculations.isPeriodicTicket
            ? `${toPrintFormat(savingTicket.travelDate)}—${toPrintFormat(savingTicket.lastTravelDate)}`
            : `${toPrintFormat(savingTicket.travelDate)}`}
        </p>
        <span className="ticket-tag-spacer" aria-hidden={true}>
          •
        </span>
        <p className="ticket-tag">{props.ticket.name}</p>
        <span className="ticket-tag-spacer" aria-hidden={true}>
          •
        </span>
        <p className="ticket-tag">{props.ticket.isWorkTrip ? 'Tjenestereise' : 'Privatreise'}</p>
      </header>
      <BaseExpand open={open} className="ticket-expand">
        {savingTicket.isWorkTrip && (
          <SmallAlertBox variant="info" style={{ marginTop: space.small }}>
            Tjenestereiser er ikke en skattepliktig fordel, og verdien av disse reisene vil derfor ikke rapporteres
            inn til selskapene.
          </SmallAlertBox>
        )}
        {isNotDistributed && props.month !== 0 && (
          <SmallAlertBox variant="info" style={{ marginTop: space.small }}>
            På grunn av en teknisk utfordring er ikke månedlig fordeling for enkelte billetter tilgjengelig på
            denne siden. Summen under gjelder derfor hele billetten uavhengig av månedene reisen er innenfor.
          </SmallAlertBox>
        )}
        <TicketDetails
          travellerName={savingTicket.name ?? ''}
          ticketReference={savingTicket.orderId}
          monthlySaving={savingDetails.saving}
          calculatedPeriod={periodCalculations}
          totalTravelDaysForTicket={totalTravelDaysForTicket}
          totalSavingForTicket={savingTicket.totalSaving.saving}
          isNotDistributed={isNotDistributed}
        />
        <SavingsDetailTable
          savingDetails={savingDetails}
          calculatedPeriod={periodCalculations}
          totalTravelDaysForTicket={totalTravelDaysForTicket}
          isWorkTrip={savingTicket.isWorkTrip}
          isNotDistributed={isNotDistributed}
          travelDate={savingTicket.travelDate}
        />
      </BaseExpand>
    </article>
  )
}
